import { HydrationBoundary, type DehydratedState } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useState } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { debugSortScoreVersionAtom, type SortScoreVersion } from '@/atoms/typesenseDebug';
import { FreeFirstSearchResults } from '@/components/FreeFirstSearchResults';
import { SEO } from '@/components/SEO';
import { ShowMoreAssetsHome } from '@/components/ShowMoreAssetsHome';
import TypesenseFilters from '@/components/TypesenseFilters';
import { ASSET_TYPES } from '@/components/TypesenseFilters/assetTypes';
import { VirtualizedTrackList } from '@/components/VirtualizedTrackList';
import { SHOW_MORE_VISITOR_PAGE_SIZE } from '@/constants/showMoreExperiment';
import { useMusicSearchWithQueue } from '@/hooks/search';
import { useShowFreeFirst } from '@/hooks/search/shared/useShowFreeFirst';
import { useQueryState } from '@/hooks/state/useQueryState';
import { useListingInfiniteScrollActive } from '@/hooks/useListingInfiniteScrollActive';
import { useShowMoreVariant } from '@/hooks/useShowMoreVariant';
import { useSchema } from '@/hooks/seo/useSchema';
import useSEO from '@/hooks/seo/useSEO';
import { useTypesenseFilterParams } from '@/hooks/useTypesenseFilterParams';
import { getPageNumberFromSlug } from '@/utils/pages';
import { getSectionCopy } from '@/utils/show-free-first/sectionCopy';
import { getBreadcrumbListSchema } from '@/utils/schema/get-breadcrumb-list-schema';
import { getImageSchema } from '@/utils/schema/get-image-schema';
import { getTrackSchema } from '@/utils/schema/get-track-schema';
import { replaceTemplateVariables } from '@/utils/template-variables';
import { AUTO_CAROUSEL_MINIMUM_MUSIC_TILES_TO_SHOW, getMusicPageFilter } from '@music-vine/setlist';
import { normalizeTracksDocumentForSearch } from '@/utils/track-documents';
import type { Collection } from '@/types/collection';
import type { MusicDocument, SearchResponseHit, SortOption } from '@music-vine/setlist';
import type { TrackUrl } from '@/types/tracks';
import type { CategoryHeader } from '@/utils/pages/api';
import type { NextPage } from 'next';
import Head from 'next/head';
import type { BreadcrumbList, ImageObject, MusicPlaylist } from 'schema-dts';
import type { Category } from '@/api/comprehensivelistings';
import { getPrimaryHeading } from '@/components/PageHeader/utils';
import { Header, HeaderBreadcrumbs, HeaderDescription, HeaderImage, HeaderRelatedSearches, HeaderSocialLinks, HeaderSubtitle, HeaderTicks, HeaderTileCarousel, HeaderTitle } from '@/components/ui/header';
import { HeaderAmbiguousLink } from '@/components/ui/header/ambiguous-link';
import type { RelatedSearchItem } from '@/components/ui/header/related-searches';
import { TileCelebrationBadge } from '@/components/ui/tile';
import { MUSIC_STYLE_PAGE_TICKS } from '@/components/ui/header/ticks';
import { useIsLoggedIn } from '@/hooks/auth';
import { getBreadcrumbs } from '@/lib/breadcrumbs/get-breadcrumbs';
import { isCollection } from '@/lib/page-is-collection';
import { useTypesenseCarouselsForPage } from '@/hooks/useTypesenseCarousels';
import { HeaderTileCarouselDebug } from '@/components/ui/header/tile-carousel-debug';
import { carouselDebugReadoutAtom, overrideCarouselSearchAtom } from '@/atoms/debug';
export type BrowseTagPageParams = {
  categorySlug: string[];
};
export type BrowseTagPaginatedPageParams = BrowseTagPageParams & {
  pageNumber: string;
};
export interface MusicPageProps {
  authState?: string;
  comprehensiveListings?: Category[] | null;
  contextPath: string;
  dehydratedState?: DehydratedState;
  pageData: CategoryHeader | Collection;
  pageTitle?: string;
  totalCount?: number;
}
const TRACK_CANONICAL_PARENT_ORDER = 999;

// Inputs resolved once by the parent so only the active layout's search hooks run.
interface MusicResultsChildProps {
  contextFilter?: string;
  contextPath: string;
  filters: string;
  pageData: CategoryHeader | Collection;
  query: string;
  sort: SortOption;
  sortScoreVersion: SortScoreVersion;
}

// Default single-stream layout (free-first OFF): infinite scroll + "show more" gate + SEO schema.
const DefaultSearchResults = ({
  query,
  filters,
  sort,
  sortScoreVersion,
  contextPath,
  contextFilter,
  pageData
}: MusicResultsChildProps) => {
  const showMoreVariant = useShowMoreVariant();
  const isLoggedIn = useIsLoggedIn();

  // Group B visitors get one page of SHOW_MORE_VISITOR_PAGE_SIZE assets and
  // must click "Show more" to engage infinite scroll (DEV-5440). Group A and
  // logged-in users keep auto infinite scroll (current behaviour).
  const isShowMoreGated = showMoreVariant === 'B' && !isLoggedIn;
  const [infiniteEnabled, setInfiniteEnabled] = useState(false);

  // Re-arm the gate whenever the underlying query changes so each distinct
  // result set starts as a single page rather than inheriting the previous
  // query's infinite mode.
  useEffect(() => {
    setInfiniteEnabled(false);
  }, [filters, sort, query]);

  // Set up music search with filters using jotai-tanstack-query
  const hookParams = {
    query,
    filters,
    fetchFreeAndPremium: true,
    autoSetupQueue: true,
    contextSlug: contextPath,
    contextFilter,
    sortScoreVersion,
    sort,
    contextPath,
    perPage: isShowMoreGated ? SHOW_MORE_VISITOR_PAGE_SIZE : undefined
  };
  const {
    data: musicData,
    hasNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    loadMoreRef,
    fetchNextPage
  } = useMusicSearchWithQueue(hookParams);
  const infiniteScrollAllowed = !isShowMoreGated || infiniteEnabled;

  // Hide the site footer while committed to the result stream, reveal it at
  // the true end (group B; group A hides the footer on these routes anyway).
  useListingInfiniteScrollActive(infiniteScrollAllowed && !!hasNextPage);

  // Extract and transform search results
  const musicResults = useMemo(() => {
    if (!musicData?.results) {
      return [];
    }
    return musicData.results;
  }, [musicData]);

  // Infinite scroll is now handled by the loadMoreRef from useMusicSearchWithQueue
  // No need for separate useInView hook or useEffect

  // Schema data for SEO - convert Typesense documents to expected Track format
  const tracksForSchema = musicResults.map(hit => ({
    id: Number(hit.document.track_id),
    name: hit.document.name || '',
    slug: ('track_slug' in hit.document ? hit.document.track_slug : '') || '',
    current_context: {
      id: Number(hit.document.track_id)
    },
    is_explicit_lyrics: false,
    is_premium: false,
    is_sfx: false,
    artist: {
      id: 0,
      // Default ID for schema purposes
      name: hit.document.contributor_name || '',
      slug: hit.document.contributor_slug || ''
    },
    track_version: [{
      id: 0,
      version_length: hit.document.version_length || 0,
      version_precise_length: hit.document.version_length || 0
    }] as [TrackUrl]
  }));
  const tagSchema = useSchema<MusicPlaylist>({
    '@type': 'MusicPlaylist',
    name: isCollection(pageData) ? pageData.name : pageData?.primary_heading_visitor ?? pageData?.name,
    description: pageData?.schema_description ?? pageData?.description ?? undefined,
    numTracks: musicResults.length,
    track: getTrackSchema(tracksForSchema)
  });

  // Generate image structured data
  const imageUrl = isCollection(pageData) ? pageData.thumbnail_image?.image_aws_url : pageData?.tile_image;
  const imageSchemaData = imageUrl ? getImageSchema(pageData?.slug || '', imageUrl) : null;
  const imageSchema = useSchema<ImageObject>(imageSchemaData || {
    '@type': 'ImageObject' as const
  });
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
        {!!pageData && <script type="application/ld+json" {...jsonLdScriptProps<MusicPlaylist>(tagSchema)} key={`tag-jsonld-${isCollection(pageData) ? pageData.name : pageData.name_sentence_case}`} />}
        {!!pageData && imageSchemaData && <script type="application/ld+json" {...jsonLdScriptProps<ImageObject>(imageSchema)} key={`image-jsonld-${pageData.slug}`} />}
      </Head>
      <SearchResults fetchNextPage={infiniteScrollAllowed ? fetchNextPage : undefined} hasNextPage={infiniteScrollAllowed && hasNextPage} isError={isError} isFetchingNextPage={isFetchingNextPage} isLoading={isLoading} results={musicResults} data-sentry-element="SearchResults" data-sentry-source-file="index.tsx" />
      {isShowMoreGated && !infiniteEnabled && !!hasNextPage && <ShowMoreAssetsHome className="mt-4" isFetchingNextPage={isFetchingNextPage} onClick={() => setInfiniteEnabled(true)} />}
      {/* Loading trigger for infinite scroll */}
      {infiniteScrollAllowed && !!hasNextPage && <div className="h-20" ref={loadMoreRef} />}
    </>;
};

// Resolves shared inputs once, then renders free-first split or default layout — never both.
const SearchResultsWithFilters = ({
  pageData,
  sort,
  contextPath
}: {
  pageData: CategoryHeader | Collection;
  sort: SortOption;
  contextPath: string;
}) => {
  // The page's membership filter is applied by the search hooks via contextSlug/contextFilter, not passed as a user filter.
  const contextFilter = getMusicPageFilter('filter' in pageData ? pageData.filter : undefined, contextPath);
  const {
    filter: combinedFilters,
    search: urlSearchTerms
  } = useTypesenseFilterParams();
  const debugSortScoreVersion = useAtomValue(debugSortScoreVersionAtom);
  const [showFreeFirst] = useShowFreeFirst();
  const searchQuery = Array.isArray(urlSearchTerms) ? urlSearchTerms.join(' ').trim() : urlSearchTerms;
  const childProps: MusicResultsChildProps = {
    query: searchQuery,
    filters: combinedFilters,
    sort,
    sortScoreVersion: debugSortScoreVersion,
    contextPath,
    contextFilter,
    pageData
  };
  return showFreeFirst ? <FreeFirstSearchResults contextFilter={contextFilter} contextPath={contextPath} contextSlug={contextPath} filters={combinedFilters} premiumTitle={getSectionCopy({
    assetType: 'music',
    pageType: isCollection(pageData) ? 'collection' : 'category'
  }).premiumHeading} query={searchQuery} sort={sort} sortScoreVersion={debugSortScoreVersion} data-sentry-element="FreeFirstSearchResults" data-sentry-component="SearchResultsWithFilters" data-sentry-source-file="index.tsx" /> : <DefaultSearchResults {...childProps} data-sentry-element="DefaultSearchResults" data-sentry-component="SearchResultsWithFilters" data-sentry-source-file="index.tsx" />;
};

// SearchResults component using virtualization
const SearchResults = memo(({
  results,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage
}: {
  results: SearchResponseHit<MusicDocument>[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}) => {
  // Convert SearchResponseHit to SearchDocument format
  const convertedResults = useMemo(() => results.map(hit => normalizeTracksDocumentForSearch({
    ...hit.document,
    asset_type: 'music',
    featured_tags: hit.document.featured_tags?.map(tag => ({
      ...tag,
      id: tag.slug || '',
      slug: tag.slug || '',
      is_qualified: 1,
      seo_relevance: 0.5
    })) || []
  })), [results]);
  return <VirtualizedTrackList fetchNextPage={fetchNextPage} hasNextPage={hasNextPage} initialRect={{
    width: 800,
    // Estimate based on container width
    height: 600 // Reasonable viewport height for SSR
  }} isError={isError} isFetchingNextPage={isFetchingNextPage} isLoading={isLoading} results={convertedResults} />;
});
SearchResults.displayName = 'SearchResults';
const BrowseMusicPage: NextPage<MusicPageProps> = memo(({
  pageData,
  dehydratedState,
  contextPath,
  pageTitle: pageTitleServer,
  totalCount,
  authState
}) => {
  const router = useRouter();
  const overrideCarouselSearch = useAtomValue(overrideCarouselSearchAtom);
  const setOverrideCarouselSearchState = useSetAtom(overrideCarouselSearchAtom);
  const setCarouselDebugReadoutAtomState = useSetAtom(carouselDebugReadoutAtom);
  // Category routes expose `categorySlug`; the shared collection routes expose
  // `collectionSlug`. Collections never paginate, but read both so pagination
  // stays correct wherever this component is mounted.
  const {
    pageNumber
  } = getPageNumberFromSlug(router.query.categorySlug ?? router.query.collectionSlug);
  const {
    generateTitleFromExistingTitle
  } = useSEO();
  const userIsLoggedIn = useIsLoggedIn();
  let displayName = pageData.name;
  if (!isCollection(pageData) && pageData.type === 'tag') {
    displayName = `${pageData.name} Music`;
  }
  const meta_title = generateTitleFromExistingTitle({
    existingTitle: pageData?.meta_title ?? undefined,
    fallbackTitle: isCollection(pageData) ? `${displayName} • Free Music For Creators ` : `Free ${displayName}`,
    pageNumber: pageNumber ? Number(pageNumber) : undefined,
    excludeTagline: true
  });
  let meta_description = replaceTemplateVariables(pageData?.meta_description ?? pageData?.description ?? undefined, {
    count: totalCount
  });
  const pageTitleClient = useMemo(() => getPrimaryHeading({
    data: pageData,
    userIsAuthenticated: userIsLoggedIn
  }), [pageData, userIsLoggedIn]);
  let description = replaceTemplateVariables(pageData?.description ?? pageData?.short_description ?? undefined, {
    count: totalCount
  });
  const overrideCarouselExcludes = isCollection(pageData) ? '' : pageData.excluded_keywords ?? '';
  useEffect(() => {
    setOverrideCarouselSearchState(prev => ({
      ...prev,
      overrideCarouselExcludes
    }));
  }, [setOverrideCarouselSearchState, overrideCarouselExcludes]);

  // Use the contextPath from getServerSideProps which handles collection vs category URLs correctly
  const fullSlug = contextPath;

  // Use useQueryState to read sort parameter consistently with TypesenseSort components
  const isValidSort = (sort: unknown): sort is SortOption => {
    const validSorts = ['relevancy', 'latest', 'undiscovered'];
    return typeof sort === 'string' && validSorts.includes(sort);
  };
  const [currentSort] = useQueryState('sort', {
    defaultValue: 'relevancy' as SortOption,
    parse: value => isValidSort(value) ? value : 'relevancy',
    serialize: value => value
  });
  // Define facets for music filters
  const musicFacets = useMemo(() => [{
    value: 'mood',
    label: 'Mood',
    display: 'popover' as const
  }, {
    value: 'genre',
    label: 'Genre',
    display: 'popover' as const
  }, {
    value: 'theme',
    label: 'Theme',
    display: 'popover' as const
  }], []);
  const trackRelatedSearches = useMemo(() => {
    let result: (string | RelatedSearchItem)[] = [];
    if (isCollection(pageData)) {
      return result;
    }

    // NOTE: Pick only those with show_in_related_search === true and those with order different from 999
    // to prevent duplicate items in the related searches
    const filteredTaxonomy = pageData?.taxonomy.filter(item => item.order !== TRACK_CANONICAL_PARENT_ORDER && item.show_in_related_search === true);
    result = filteredTaxonomy.map(taxonomy => ({
      label: taxonomy.name,
      href: taxonomy.url
    }));
    return result;
  }, [pageData]);
  const trackBreadcrumbs = useMemo(() => {
    const result = getBreadcrumbs({
      data: pageData,
      assetType: 'music'
    });
    return result;
  }, [pageData]);

  // Note: Schema data will be handled inside SearchResultsWithFilters component
  // since it needs access to the actual search results

  // Update description for tags if not provided
  if (!(description || isCollection(pageData)) && pageData.type === 'tag' && pageData?.name_sentence_case) {
    description = `Free ${pageData?.name_sentence_case} tracks. Download the best royalty free ${pageData?.name_sentence_case} music for YouTube, Twitch, Instagram, TikTok, podcasts and more. Uppbeat is the free music platform for creators.`;
  }
  if (!meta_description) {
    meta_description = description;
  }
  const showSeoTicks = useMemo(() => {
    const isPersonalized = 'pageType' in pageData && pageData.pageType === 'personalized';

    // Roles only restore after mount, so without the server's authState logged-in HTML would ship ticks and then drop them
    const isAuthenticated = userIsLoggedIn || authState === 'authenticated';
    return !(isAuthenticated || isPersonalized);
  }, [userIsLoggedIn, authState, pageData]);
  const seoTickContent = useMemo(() => {
    if (isCollection(pageData)) {
      return;
    }
    if (pageData?.target_ksp) {
      return [pageData.target_ksp, 'No copyright issues', 'Download now'];
    }
    return MUSIC_STYLE_PAGE_TICKS;
  }, [pageData]);
  const ambiguousLinks = useMemo(() => {
    if (isCollection(pageData)) {
      return [];
    }
    return pageData?.ambiguous_links ?? [];
  }, [pageData]);
  const carouselQueryOptions = {
    enabled: !isCollection(pageData) && !!fullSlug
  };
  const {
    data: processedCarouselResult
  } = useTypesenseCarouselsForPage({
    library: 'music',
    pageData,
    queryOptions: carouselQueryOptions,
    carouselSearchDebugOverride: overrideCarouselSearch
  });
  const pageCarousels = processedCarouselResult?.carousels;
  if (processedCarouselResult?.debug_info) {
    setCarouselDebugReadoutAtomState(processedCarouselResult?.debug_info);
  }
  const ShowCarouselDescription = useMemo(() => {
    if (isCollection(pageData) || !pageCarousels) {
      return false;
    }
    let showCarouselDescription = true;
    for (const pageCarousel of pageCarousels) {
      // If any of the carousels have descriptions set to hidden, don't show descriptions
      if (!(pageCarousel.show_carousel_description || overrideCarouselSearch.carouselDebugInfoVisible)) {
        showCarouselDescription = false;
      }
    }
    return showCarouselDescription;
  }, [pageCarousels, overrideCarouselSearch.carouselDebugInfoVisible, pageData.slug, pageData]);
  const SliderCarouselData = useMemo(() => {
    if (isCollection(pageData) || !pageCarousels) {
      return [];
    }
    const result = pageCarousels.flatMap(item => item.tiles).map(item => ({
      id: String(item.slug),
      name: item.title,
      order: item.order,
      tile_image: item.image,
      url: item.slug,
      description: item.description ?? undefined,
      debug_info: item.debug_info
    }));
    return result;
  }, [pageCarousels, pageData.slug, pageData]);

  // Removed: window.scrollTo({ top: 0 }) was overriding native scroll
  // restoration on back navigation. Next.js handles scroll-to-top on
  // forward navigation automatically.

  const headerImageSrc = isCollection(pageData) ? pageData.thumbnail_image?.image_aws_url : pageData.tile_image;
  const PAGE_TITLE = pageTitleServer ?? pageTitleClient;
  return <HydrationBoundary state={dehydratedState}>
        <SEO description={meta_description} title={meta_title} />
        {!!trackBreadcrumbs && <Head>
            <script type="application/ld+json" {...jsonLdScriptProps<BreadcrumbList>(getBreadcrumbListSchema(trackBreadcrumbs))} key={`breadcrumb-jsonld-${pageData.slug}`} />
          </Head>}
        <Header separator ticks={showSeoTicks}>
          {isCollection(pageData) && headerImageSrc && <HeaderImage src={headerImageSrc} alt={`${pageData?.inspired_by ? 'Inspired by ' : ''}${PAGE_TITLE} cover image`} containerClassName="hidden md:block" overlay={pageData.celebration_date ? <TileCelebrationBadge badgeColor={pageData.overlay_colour} date={pageData.celebration_date} /> : null} />}
          <HeaderTitle inspiredBy={pageData?.inspired_by}>
            {PAGE_TITLE}
          </HeaderTitle>
          <HeaderSocialLinks contributor={pageData?.partner_name} discord={pageData?.discord_url} instagram={pageData?.instagram_url} tiktok={pageData?.tiktok_url} twitter={pageData?.twitter_url} youtube={pageData?.youtube_url} />

          <HeaderDescription>{description}</HeaderDescription>
          {!!trackBreadcrumbs && <HeaderBreadcrumbs {...trackBreadcrumbs} />}
          {!!trackRelatedSearches?.length && <HeaderRelatedSearches searches={trackRelatedSearches} />}
          {showSeoTicks && <HeaderTicks content={seoTickContent} />}
          {ambiguousLinks && <HeaderSubtitle>
              {ambiguousLinks.map(link => <HeaderAmbiguousLink data={link} key={link.url} />)}
            </HeaderSubtitle>}
          {!isCollection(pageData) && !!overrideCarouselSearch.carouselDebugInfoVisible && <HeaderTileCarouselDebug items={SliderCarouselData} showDescriptions={ShowCarouselDescription} minimumTilesToShow={AUTO_CAROUSEL_MINIMUM_MUSIC_TILES_TO_SHOW} />}
          {!isCollection(pageData) && SliderCarouselData.length > 0 && <HeaderTileCarousel items={SliderCarouselData} showDescriptions minimumTilesToShow={AUTO_CAROUSEL_MINIMUM_MUSIC_TILES_TO_SHOW} mode={ShowCarouselDescription ? 'full' : 'title-only'} />}
        </Header>

        {/* Music filters and track listing with simplified context */}
        <TypesenseFilters assetType={ASSET_TYPES.MUSIC} facets={musicFacets} isSticky={true} pageFilter={getMusicPageFilter('filter' in pageData ? pageData.filter : undefined, fullSlug)} showSearch={true} showSort={true} sortRelevanceLabelOverride={isCollection(pageData) ? 'As curated' : undefined} stickyContainerClassName="top-[51px] md:top-[67px]">
          <div className="mx-auto w-full space-y-4">
            <SearchResultsWithFilters contextPath={fullSlug} pageData={pageData} sort={currentSort} />
          </div>
        </TypesenseFilters>
        {/* 21/10/25 - James is doing a crawl to see how this removal affects SEO, may be permanent in the future - SB */}
        {/* {comprehensiveListings && (
          <ComprehensiveListings
            initialData={comprehensiveListings}
            variant="music"
            onlyNewUsers
          />
         )} */}
      </HydrationBoundary>;
});
BrowseMusicPage.displayName = 'BrowseMusicPage';
export default BrowseMusicPage;